import React, { useState, useEffect, useMemo } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CreateIcon from '@mui/icons-material/Create';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SearchIcon from '@mui/icons-material/Search';
import { ubicacionesResources } from "../endpoints";
import DialoAgregarEditarUbicacion from "./DialogAgregarEditarUbicacion";
import DialogEliminar from "./DialogEliminar";
import SnackbarAlert from "./SnackbarAlert";

const TablaUbicaciones = () => {
    // Estados principales
    const [datosOriginales, setDatosOriginales] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [selectedRowId, setSelectedRowId] = useState(null);
    
    // Estado del buscador
    const [busqueda, setBusqueda] = useState('');
    
    // Estado para controlar qué nodos padres están colapsados (guardamos sus IDs)
    const [nodosColapsados, setNodosColapsados] = useState(new Set());

    // Estados de control de Diálogos
    const [open, setOpen] = useState(false);
    const [eliminar, setEliminar] = useState(false);
    const [idPadre, setIdPadre] = useState(null);
    const [title, setTitle] = useState("");
    const [ubicacion, setUbicacion] = useState(null);
    const [idUbicacion, setIdUbicacion] = useState(0);
    const [idRango, setIdRango] = useState(0);
    const [content, setContent] = useState("");

    // Estados de Snackbar
    const [openSnack, setOpenSnack] = useState(false);
    const [severity, setSeverity] = useState(null);
    const [message, setMessage] = useState(null);

    const obtenerDatos = () => {
        setCargando(true);
        setOpenSnack(false);
        setSeverity(null);
        setMessage(null);

        ubicacionesResources.obtenerUbicaciones()
            .then(async response => {
                if (response) return await response.json();
            })
            .then(result => {
                if (result.status_code === 200) {
                    setDatosOriginales(result.ubicaciones);
                } else if (result.status_code === 401) {
                    setOpenSnack(true);
                    setSeverity(result.severity);
                    setMessage(result.message);
                } else {
                    throw new Error('Something went wrong ...');
                }
            })
            .catch(error => console.log(error))
            .finally(() => setCargando(false));
    };

    useEffect(() => {
        obtenerDatos();
    }, []);

    const toggleColapso = (id) => {
        setNodosColapsados(prev => {
            const nuevoSet = new Set(prev);
            if (nuevoSet.has(id)) {
                nuevoSet.delete(id);
            } else {
                nuevoSet.add(id);
            }
            return nuevoSet;
        });
    };

    // Lógica de procesamiento e inclusión del filtro de búsqueda manteniendo jerarquía
    const datosVisibles = useMemo(() => {
        const query = busqueda.trim().toLowerCase();

        // Caso 1: Sin búsqueda activa (muestra el árbol respetando colapsos)
        if (!query) {
            const procesarArbol = (nodos, padreId = null, depth = 0) => {
                let resultado = [];
                const hijos = nodos.filter(n => {
                    if (padreId === null) {
                        return !n.padre || !nodos.some(all => all.id === n.padre);
                    }
                    return n.padre === padreId;
                });

                hijos.forEach(hijo => {
                    const tieneHijos = nodos.some(n => n.padre === hijo.id);
                    resultado.push({ ...hijo, depth, tieneHijos });

                    if (!nodosColapsados.has(hijo.id)) {
                        const nietos = procesarArbol(nodos, hijo.id, depth + 1);
                        resultado = resultado.concat(nietos);
                    }
                });

                return resultado;
            };

            return procesarArbol(datosOriginales);
        }

        // Caso 2: Con búsqueda activa (respetando si el nodo padre fue colapsado)
        const mapaNodos = new Map(datosOriginales.map(nodo => [nodo.id, nodo]));

        // 1. Identificar coincidencia
        const idsCoincidentes = datosOriginales.filter(item => {
            const descripcionMatch = item.descripcion?.toLowerCase().includes(query);
            const comentariosMatch = item.comentarios?.toLowerCase().includes(query);
            const tagInicialMatch = item.tag_inicial?.toString().toLowerCase().includes(query);
            const tagFinalMatch = item.tag_final?.toString().toLowerCase().includes(query);
            return descripcionMatch || comentariosMatch || tagInicialMatch || tagFinalMatch;
        }).map(item => item.id);

        // 2. Obtener ancestros
        const idsAIncluir = new Set();
        idsCoincidentes.forEach(id => {
            let actual = mapaNodos.get(id);
            while (actual) {
                idsAIncluir.add(actual.id);
                actual = actual.padre ? mapaNodos.get(actual.padre) : null;
            }
        });

        // 3. Procesar árbol filtrado respetando 'nodosColapsados'
        const procesarArbolFiltrado = (nodos, padreId = null, depth = 0) => {
            let resultado = [];

            const hijos = nodos.filter(n => {
                if (!idsAIncluir.has(n.id)) return false;
                if (padreId === null) {
                    return !n.padre || !idsAIncluir.has(n.padre);
                }
                return n.padre === padreId;
            });

            hijos.forEach(hijo => {
                const tieneHijosVisibles = nodos.some(n => idsAIncluir.has(n.id) && n.padre === hijo.id);

                resultado.push({ ...hijo, depth, tieneHijos: tieneHijosVisibles });

                // 💡 AQUÍ ESTÁ EL CAMBIO: Solo procesa e incluye los nietos si el padre NO está colapsado
                if (!nodosColapsados.has(hijo.id)) {
                    const nietos = procesarArbolFiltrado(nodos, hijo.id, depth + 1);
                    resultado = resultado.concat(nietos);
                }
            });

            return resultado;
        };

        return procesarArbolFiltrado(datosOriginales);
    }, [datosOriginales, nodosColapsados, busqueda]);

    const handleClose = () => {
        setOpen(false);
        setUbicacion(null);
        setEliminar(false);
        setSelectedRowId(null);
    };

    const refrescarDatos = (estado, sev) => {
        setOpen(false);
        setUbicacion(null);
        setEliminar(false);
        setOpenSnack(false);
        setSeverity(null);
        setMessage(null);
        setSelectedRowId(null);

        if (sev === "success") {
            obtenerDatos();
        }
        setOpenSnack(true);
        setSeverity(sev);
        setMessage(estado);
    };

    const agregarUbicacion = (id) => {
        setOpen(true);
        setIdPadre(id);
        setTitle("Agregar ubicación");
        setOpenSnack(false);
    };

    const editarUbicacion = (row) => {
        setOpen(true);
        setIdPadre(row.id);
        setTitle("Editar ubicación");
        setUbicacion(row);
        setOpenSnack(false);
    };

    const eliminarUbicacion = (row) => {
        setEliminar(true);
        setIdUbicacion(row.id);
        setIdRango(row.id_rango);
        setTitle("Eliminar ubicación");
        setContent("Esta acción es irreversible. ¿Está seguro que desea eliminar la ubicación?");
        setOpenSnack(false);
    };

    const columns = [
        {
            field: 'descripcion',
            headerName: 'Descripción',
            minWidth: 500,
            sortable: false,
            renderCell: (params) => {
                const { depth, tieneHijos, id, descripcion } = params.row;
                const estaColapsado = nodosColapsados.has(id);

                return (
                    <Box sx={{ pl: depth * 3, display: 'flex', alignItems: 'center', height: '100%' }}>
                        {tieneHijos ? (
                            <IconButton 
                                size="small" 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleColapso(id);
                                }}
                                sx={{ mr: 0.5, p: 0.2 }}
                            >
                                {estaColapsado ? <KeyboardArrowRightIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                            </IconButton>
                        ) : (
                            <Box sx={{ width: 24 }} />
                        )}
                        {descripcion}
                    </Box>
                );
            }
        },
        { field: 'comentarios', headerName: 'Comentario Ubicación', minWidth: 400, sortable: false },
        { field: 'tag_inicial', headerName: 'Tag inicial', minWidth: 150, sortable: false },
        { field: 'tag_final', headerName: 'Tag final', minWidth: 150, sortable: false },
        { field: 'comentario_rango', headerName: 'Comentario Rango', minWidth: 400, sortable: false },
        {
            field: 'acciones',
            headerName: 'Acciones',
            minWidth: 180,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const rowData = params.row;
                const disableAdd = rowData.id === -1 || rowData.tag_inicial !== null || rowData.descripcion === 'EXHIBICION TEMPORAL' || rowData.descripcion === 'ENVASES AGREGADOS';
                const disableEditDelete = rowData.inicial === true || rowData.descripcion === 'EXHIBICION TEMPORAL' || rowData.descripcion === 'ENVASES AGREGADOS';

                return (
                    <Box>
                        <IconButton onClick={() => agregarUbicacion(rowData.id)} disabled={disableAdd} color="primary">
                            <AddCircleIcon />
                        </IconButton>
                        <IconButton onClick={() => editarUbicacion(rowData)} disabled={disableEditDelete} color="primary">
                            <CreateIcon />
                        </IconButton>
                        <IconButton onClick={() => eliminarUbicacion(rowData)} disabled={disableEditDelete} color="error">
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                );
            }
        }
    ];

    return (
        <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Input de Buscador */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-start' }}>
                <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Buscar ubicación, comentario, tag..."
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    sx={{ width: 350 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <Box sx={{ flexGrow: 1, width: '100%' }}>
                <DataGrid
                    rows={datosVisibles} 
                    disableColumnMenu
                    columns={columns}
                    loading={cargando}
                    pageSizeOptions={[10, 25, 50, 100]}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 25 } },
                    }}
                    density="compact"
                    disableRowSelectionOnClick
                    onRowClick={(params) => setSelectedRowId(params.id)}
                    getRowClassName={(params) => params.id === selectedRowId ? 'selected-row-style' : ''}
                    sx={{
                        '& .selected-row-style': { backgroundColor: '#dbe9fc !important' },
                        '& .MuiDataGrid-cell': {
                            fontSize: '15px',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            display: 'flex',
                        },
                    }}
                    localeText={{
                        noRowsLabel: 'No hay datos para mostrar',
                        toolbarSearchPlaceholder: 'Buscar',
                    }}
                />
            </Box>

            {open && (
                <DialoAgregarEditarUbicacion 
                    refrescar={refrescarDatos} 
                    ubicacion={ubicacion} 
                    title={title} 
                    padre={idPadre} 
                    handleClose={handleClose}
                />
            )}
            {eliminar && (
                <DialogEliminar 
                    origen={"Ubicación"} 
                    handleClose={handleClose} 
                    refrescar={refrescarDatos} 
                    content={content} 
                    title={title} 
                    id={idUbicacion}  
                    idRango={idRango}  
                />
            )}
            {openSnack && (
                <SnackbarAlert severity={severity} message={message}/>
            )}
        </div>
    );
};

export default TablaUbicaciones;